import { useState, useRef, useEffect } from "react";
import { Edit, Scissors, Copy, Clipboard, Undo2, Redo2 } from "lucide-react";
import Modal from "../Modal/Modal";

interface ScratchPadModalProps {
  isOpen: boolean;
  onClose: () => void;
  content: string;
  onChange: (content: string) => void;
}

const ScratchPadModal = ({
  isOpen,
  onClose,
  content,
  onChange,
}: ScratchPadModalProps) => {
  const [localContent, setLocalContent] = useState(content);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [history, setHistory] = useState<string[]>([content]);
  const [historyIndex, setHistoryIndex] = useState(0);

  // Sync localContent with prop content when it changes externally
  useEffect(() => {
    if (content !== localContent) {
      setLocalContent(content);
      setHistory([content]);
      setHistoryIndex(0);
    }
  }, [content]);

  const handleContentChange = (newContent: string) => {
    setLocalContent(newContent);
    onChange(newContent);

    // Update history for undo/redo
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(newContent);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  const handleCut = () => {
    const textarea = textareaRef.current;
    if (textarea && textarea.selectionStart !== textarea.selectionEnd) {
      const selectedText = textarea.value.substring(
        textarea.selectionStart,
        textarea.selectionEnd
      );
      navigator.clipboard.writeText(selectedText);
      const newContent =
        textarea.value.substring(0, textarea.selectionStart) +
        textarea.value.substring(textarea.selectionEnd);
      handleContentChange(newContent);
      textarea.focus();
    }
  };

  const handleCopy = () => {
    const textarea = textareaRef.current;
    if (textarea && textarea.selectionStart !== textarea.selectionEnd) {
      const selectedText = textarea.value.substring(
        textarea.selectionStart,
        textarea.selectionEnd
      );
      navigator.clipboard.writeText(selectedText);
    }
  };

  const handlePaste = async () => {
    const textarea = textareaRef.current;
    if (textarea) {
      try {
        const text = await navigator.clipboard.readText();
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const newContent =
          textarea.value.substring(0, start) +
          text +
          textarea.value.substring(end);
        handleContentChange(newContent);

        // Set cursor position after pasted text
        setTimeout(() => {
          textarea.focus();
          textarea.setSelectionRange(start + text.length, start + text.length);
        }, 0);
      } catch (err) {
        console.error("Failed to paste:", err);
      }
    }
  };

  const handleUndo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      const previousContent = history[newIndex];
      setLocalContent(previousContent);
      onChange(previousContent);
    }
  };

  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      const nextContent = history[newIndex];
      setLocalContent(nextContent);
      onChange(nextContent);
    }
  };

  const handleClose = () => {
    onChange(localContent);
    onClose();
  };

  return (
    <Modal
      header={
        <div className="flex items-center gap-2">
          <Edit size={16} className="text-gray-700" />
          <span>Scratch Pad</span>
        </div>
      }
      isOpen={isOpen}
      closeModal={handleClose}
      resizable={true}
    >
      <div className="flex flex-col" style={{
        minWidth: "50svw",
        minHeight: "50svh"
      }}>
        {/* Toolbar */}
        <div className="flex items-center gap-1 px-2 py-1.5 bg-[#F2F2F2] border-b border-gray-300 flex-shrink-0">
          <button
            onClick={handleCut}
            className="px-2 py-1.5 hover:bg-gray-300 flex items-center gap-1.5 transition-colors"
            title="Cut (Ctrl+X)"
          >
            <Scissors size={16} className="text-gray-700" />
            <span className="text-sm text-gray-700">Cut</span>
          </button>
          <button
            onClick={handleCopy}
            className="px-2 py-1.5 hover:bg-gray-300 flex items-center gap-1.5 transition-colors"
            title="Copy (Ctrl+C)"
          >
            <Copy size={16} className="text-gray-700" />
            <span className="text-sm text-gray-700">Copy</span>
          </button>
          <button
            onClick={handlePaste}
            className="px-2 py-1.5 hover:bg-gray-300 flex items-center gap-1.5 transition-colors"
            title="Paste (Ctrl+V)"
          >
            <Clipboard size={16} className="text-gray-700" />
            <span className="text-sm text-gray-700">Paste</span>
          </button>
          <button
            onClick={handleUndo}
            disabled={historyIndex <= 0}
            className="px-2 py-1.5 hover:bg-gray-300 flex items-center gap-1.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            title="Undo (Ctrl+Z)"
          >
            <Undo2 size={16} className="text-gray-700" />
            <span className="text-sm text-gray-700">Undo</span>
          </button>
          <button
            onClick={handleRedo}
            disabled={historyIndex >= history.length - 1}
            className="px-2 py-1.5 hover:bg-gray-300 flex items-center gap-1.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            title="Redo (Ctrl+Y)"
          >
            <Redo2 size={16} className="text-gray-700" />
            <span className="text-sm text-gray-700">Redo</span>
          </button>
        </div>

        {/* Text Area */}
        <div
          className="flex-1 bg-white overflow-hidden"
          style={{ minHeight: 0 }}
        >
          <textarea
            ref={textareaRef}
            value={localContent}
            onChange={(e) => handleContentChange(e.target.value)}
            className="w-full h-full p-3 resize-none focus:outline-none border-0 bg-white text-gray-800"
            placeholder=""
            style={{
              width: "100%",
              height: "100%",
              fontFamily: "inherit",
              fontSize: "14px",
              lineHeight: "1.5",
            }}
          />
        </div>
      </div>
    </Modal>
  );
};

export default ScratchPadModal;
